import React from "react";

/**
 * GlobalErrorBoundary
 * Catches any unhandled React render / lifecycle errors and displays a
 * friendly recovery screen instead of a blank / black page.
 */
export class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this._retryCount = 0;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Uncaught error:", error, errorInfo);
  }

  handleRetry = () => {
    this._retryCount += 1;
    this.setState({ hasError: false, error: null });
  };

  handleHardReload = () => { window.location.reload(); };
  handleGoHome = () => { window.location.href = "/"; };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback !== undefined) {
      return this.props.fallback;
    }

    const errMsg = this.state.error?.message || "Unknown error";
    const errStack = this.state.error?.stack || "";

    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <div style={styles.iconWrap}>
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none"
              stroke="#f87171" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <h1 style={styles.title}>Something went wrong</h1>
          <p style={styles.subtitle}>
            The page ran into an unexpected error. Your session and data are safe.
          </p>
          {errMsg && <pre style={styles.errBox}>{errMsg}</pre>}
          {errStack && (
            <details style={{ textAlign: "left", marginBottom: "20px", color: "#64748b", fontSize: "11px" }}>
              <summary style={{ cursor: "pointer", color: "#94a3b8" }}>Technical Details</summary>
              <pre style={{ ...styles.errBox, marginTop: "8px", maxHeight: "150px" }}>{errStack}</pre>
            </details>
          )}
          <div style={styles.btnRow}>
            <button style={{...styles.btn, ...styles.btnPrimary}} onClick={this.handleRetry}>
              ↺ Try Again
            </button>
            <button style={{...styles.btn, ...styles.btnSecondary}} onClick={this.handleHardReload}>
              ⟳ Reload Page
            </button>
            <button style={{...styles.btn, ...styles.btnGhost}} onClick={this.handleGoHome}>
              ⌂ Go Home
            </button>
          </div>
          <p style={styles.hint}>If this keeps happening, please contact support.</p>
        </div>
      </div>
    );
  }
}

const styles = {
  overlay: {
    minHeight:"100vh", background:"linear-gradient(135deg,#0A0E17 0%,#0f172a 100%)",
    display:"flex", alignItems:"center", justifyContent:"center",
    fontFamily:"'Inter','Segoe UI',Arial,sans-serif", padding:"24px",
  },
  card: {
    background:"rgba(19,27,44,0.97)", border:"1px solid rgba(212,175,55,0.25)",
    borderRadius:"20px", padding:"48px 40px", maxWidth:"480px", width:"100%",
    textAlign:"center", boxShadow:"0 32px 80px rgba(0,0,0,0.6)",
  },
  iconWrap: { marginBottom:"24px", display:"flex", justifyContent:"center" },
  title: { fontSize:"24px", fontWeight:"700", color:"#f1f5f9", margin:"0 0 12px", letterSpacing:"-0.3px" },
  subtitle: { fontSize:"15px", color:"#94a3b8", margin:"0 0 24px", lineHeight:"1.6" },
  errBox: {
    background:"rgba(248,113,113,0.08)", border:"1px solid rgba(248,113,113,0.2)",
    borderRadius:"8px", padding:"12px 16px", fontSize:"12px", color:"#fca5a5",
    textAlign:"left", marginBottom:"24px", overflowX:"auto", whiteSpace:"pre-wrap", wordBreak:"break-word",
  },
  btnRow: { display:"flex", gap:"10px", justifyContent:"center", flexWrap:"wrap", marginBottom:"20px" },
  btn: {
    border:"none", borderRadius:"10px", padding:"10px 20px", fontSize:"14px",
    fontWeight:"600", cursor:"pointer", transition:"opacity 0.2s", letterSpacing:"0.2px",
  },
  btnPrimary: { background:"linear-gradient(135deg,#D4AF37,#B8860B)", color:"#0A0E17" },
  btnSecondary: { background:"rgba(100,116,139,0.2)", color:"#cbd5e1", border:"1px solid rgba(100,116,139,0.35)" },
  btnGhost: { background:"transparent", color:"#64748b", border:"1px solid rgba(100,116,139,0.2)" },
  hint: { fontSize:"12px", color:"#475569", margin:0 },
};

export default GlobalErrorBoundary;
